import { Injectable } from '@angular/core';
import { MessageNotificationService } from 'message-notification-service-lib';
import { from, Observable, of } from 'rxjs';
import { concatMap, map, switchMap, tap, toArray } from 'rxjs/operators';
import { IClientConfig } from '../types/client-config';
import { IQuestion, IQuestionPackCreate } from '../types/IQuestionManager';
import { NotetakerCallsService } from './notetaker-calls.service';
import { QuestionPackService } from './questionPack.service';

@Injectable({ providedIn: 'root' })
export class QuestionPackManagerService {
  constructor(
    private calls: NotetakerCallsService,
    private packService: QuestionPackService,
    private messages: MessageNotificationService,
  ) {}

  ensurePackWithDefaultQuestions(
    clientConfig: IClientConfig,
    incomingCorpId: string,
    entityTypes: string[] = ['Candidate', 'ClientContact']
  ): Observable<string> {
    const rawCorpId = incomingCorpId.toString().replace(/^bullhorn-/, '');

    const namespaceId = `bullhorn-${rawCorpId}`;

    return this.calls.getQuestionsByCorp(namespaceId).pipe(
      switchMap(allQ => {
        const existing = allQ.find(q => q.sortKey?.startsWith('QuestionPack-'));
        if (existing) {
          return of(existing.sortKey);
        }

        const newPack: IQuestionPackCreate = {
          corpId:        namespaceId,
          corporationId: rawCorpId,
          namespace:     'bullhorn',
          name:          'Question Pack 1',
          sortKey:       `QuestionPack-${crypto.randomUUID()}`,
        };


        return this.calls.saveQuestion(newPack).pipe(
          tap(() => this.messages.success('Question pack created')),
          switchMap(() =>
            this.packService.updateClientConfigWithQuestionPack(clientConfig, newPack)
          ),
          switchMap(() => this.createDefaults(newPack, entityTypes)),
          map(() => newPack.sortKey)
        );
      })
    );
  }

  private createDefaults(
    pack: IQuestionPackCreate,
    entityTypes: string[]
  ): Observable<IQuestion[]> {
    const guid = pack.sortKey.replace('QuestionPack-', '');
    const templates = [
      {
        question: 'Summarise this call and pull out some key highlights',
        type: 'summary',
        destination: '',
        additionalRestrictions: '',
        style: 'SUMMARY',
      },
    ];
    const tasks = entityTypes.flatMap(et =>
      templates.map(meta => ({ entityType: et, meta }))
    );

    return from(tasks).pipe(
      concatMap(({ entityType, meta }) => {
        const q: IQuestion = {
          corpId:        pack.corpId,
          corporationId: pack.corporationId,
          namespace:     pack.namespace,
          entityType,
          sortKey:       `Question-${guid}-${entityType}-${crypto.randomUUID()}`,
          meta:          JSON.stringify(meta),
        };
        return this.calls.saveQuestion(q).pipe(
          tap(() =>
            console.log('successfully saved question')
          ),
          map(() => q)
        );
      }),
      toArray()
    );
  }
}
